import React, { useCallback, useEffect, useState } from 'react';
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { useSystem } from '@ohif/core';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ohif/ui-next';

import ToggleSwitch from '../common/ToggleSwitch';
import AddWorklistPriorityRuleModal from '../modals/AddWorklistPriorityRuleModal';
import { ICON_STROKE_WIDTH } from '../../constants';
import {
  formatCriterionTypeLabel,
  formatCriterionValueDisplay,
} from '../../utils/worklistPriorityUtils';
import {
  deleteWorklistPriorityRule,
  listWorklistPriorityRules,
  reorderWorklistPriorityRules,
  updateWorklistPriorityRule,
  type WorklistPriorityRule,
} from '../../services/worklistPriorityRuleService';
import { useReorderableRows } from '../../hooks';
import { moveItem } from '../../utils/moveItem';

interface WorklistPrioritySectionProps {
  facilityId: number;
}

export default function WorklistPrioritySection({
  facilityId,
}: Readonly<WorklistPrioritySectionProps>) {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const [rules, setRules] = useState<WorklistPriorityRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<WorklistPriorityRule | null>(null);
  const [rulePendingDelete, setRulePendingDelete] = useState<WorklistPriorityRule | null>(null);

  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await listWorklistPriorityRules(facilityId);
    if (res.success && Array.isArray(res.data)) {
      setRules(res.data);
    } else {
      setRules([]);
      setError(res.error || 'Failed to load worklist priority rules');
    }
    setLoading(false);
  }, [facilityId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleToggleActive = async (rule: WorklistPriorityRule, checked: boolean) => {
    setTogglingId(rule.id);
    const res = await updateWorklistPriorityRule(rule.id, {
      facilityId,
      isActive: checked,
    });
    setTogglingId(null);

    if (res.success && res.data) {
      setRules(prev => prev.map(item => (item.id === rule.id ? res.data! : item)));
      return;
    }

    setError(res.error || 'Failed to update rule status');
  };

  const handleDelete = async (rule: WorklistPriorityRule) => {
    setDeletingId(rule.id);
    setError(null);
    const res = await deleteWorklistPriorityRule(rule.id, facilityId);
    setDeletingId(null);
    setRulePendingDelete(null);

    if (res.success) {
      setRules(prev => prev.filter(item => item.id !== rule.id));
      uiNotificationService?.show({
        title: 'Rule deleted',
        message: `${rule.name} was removed.`,
        type: 'success',
        duration: 2500,
      });
      return;
    }

    setError(res.error || 'Failed to delete rule');
  };

  const persistReorder = useCallback(
    async (nextRules: WorklistPriorityRule[], previousRules: WorklistPriorityRule[]) => {
      setReordering(true);
      setRules(nextRules);
      const res = await reorderWorklistPriorityRules(
        nextRules.map(rule => rule.id),
        facilityId
      );
      setReordering(false);

      if (res.success && Array.isArray(res.data)) {
        setRules(res.data);
        return;
      }

      setRules(previousRules);
      setError(res.error || 'Failed to reorder rules');
    },
    [facilityId]
  );

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      const previous = rules;
      const next = moveItem(rules, fromIndex, toIndex);
      void persistReorder(next, previous);
    },
    [rules, persistReorder]
  );

  const { dragIndex, getRowProps, getHandleProps } = useReorderableRows({
    disabled: reordering,
    onReorder: handleReorder,
  });

  const openAddModal = () => {
    setEditingRule(null);
    setModalOpen(true);
  };

  const openEditModal = (rule: WorklistPriorityRule) => {
    setEditingRule(rule);
    setModalOpen(true);
  };

  return (
    <section className="esc-step-card">
      <header className="esc-step-header">
        <div className="esc-step-title-row">
          <h2 className="esc-step-title">Worklist prioritization rules</h2>
          <span className="esc-count-badge">{rules.length}</span>
        </div>
        <p className="esc-step-subtitle">
          Studies are ordered by the first matching rule below. Drag rows to set rule precedence —
          top rule wins. Higher-priority studies appear at the top of the radiologist worklist.
        </p>
      </header>

      <div className="esc-toolbar esc-toolbar--contacts">
        <div className="esc-toolbar-actions">
          <button
            type="button"
            className="esc-btn esc-btn--primary esc-btn--inline"
            onClick={openAddModal}
          >
            <Plus
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            Add priority rule
          </button>
        </div>
      </div>

      {error ? (
        <p
          className="esc-error-banner"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      <div className="esc-table-wrap">
        <table className="esc-table">
          <thead>
            <tr>
              <th
                scope="col"
                className="esc-table-drag-col"
              >
                <span className="sr-only">Order</span>
              </th>
              <th scope="col">#</th>
              <th scope="col">Name</th>
              <th scope="col">Criterion</th>
              <th scope="col">Value</th>
              <th scope="col">Active</th>
              <th
                className="esc-table-actions-col"
                aria-label="Actions"
              />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="esc-table-empty"
                >
                  Loading worklist priority rules…
                </td>
              </tr>
            ) : rules.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="esc-table-empty"
                >
                  No prioritization rules configured. Studies will use default date/time ordering.
                </td>
              </tr>
            ) : (
              rules.map((rule, index) => (
                <tr
                  key={rule.id}
                  {...getRowProps(index)}
                  className={dragIndex === index ? 'esc-table-row--dragging' : undefined}
                >
                  <td className="esc-table-drag-col">
                    <span
                      className="esc-drag-handle"
                      aria-hidden="true"
                      {...getHandleProps(index)}
                    >
                      <GripVertical
                        size={16}
                        strokeWidth={ICON_STROKE_WIDTH}
                      />
                    </span>
                  </td>
                  <td className="esc-table-index">{index + 1}</td>
                  <td className="esc-table-name">{rule.name}</td>
                  <td>{formatCriterionTypeLabel(rule.criterionType)}</td>
                  <td>{formatCriterionValueDisplay(rule)}</td>
                  <td className="esc-table-active-col">
                    <ToggleSwitch
                      checked={rule.isActive}
                      disabled={togglingId === rule.id || reordering}
                      onChange={checked => handleToggleActive(rule, checked)}
                    />
                  </td>
                  <td className="esc-table-actions-col">
                    <div className="esc-table-actions">
                      <button
                        type="button"
                        className="esc-icon-btn"
                        aria-label={`Edit ${rule.name}`}
                        onClick={() => openEditModal(rule)}
                      >
                        <Pencil
                          size={16}
                          strokeWidth={ICON_STROKE_WIDTH}
                        />
                      </button>
                      <button
                        type="button"
                        className="esc-icon-btn esc-icon-btn--danger"
                        aria-label={`Delete ${rule.name}`}
                        disabled={deletingId === rule.id}
                        onClick={() => setRulePendingDelete(rule)}
                      >
                        <Trash2
                          size={16}
                          strokeWidth={ICON_STROKE_WIDTH}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AddWorklistPriorityRuleModal
        isOpen={modalOpen}
        facilityId={facilityId}
        editingRule={editingRule}
        onClose={() => setModalOpen(false)}
        onSubmit={loadRules}
      />

      <Dialog
        open={Boolean(rulePendingDelete)}
        onOpenChange={open => !open && !deletingId && setRulePendingDelete(null)}
      >
        <DialogContent className="esc-contact-dialog max-w-[480px] bg-white text-[#0a0a0a]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Delete priority rule</DialogTitle>
          </DialogHeader>

          <div className="esc-activate-dialog-body">
            <p>
              Are you sure you want to delete{' '}
              <strong>{rulePendingDelete?.name ?? 'this rule'}</strong>? This action cannot be
              undone.
            </p>
          </div>

          <div className="esc-dialog-actions">
            <button
              type="button"
              className="esc-btn esc-btn--secondary"
              disabled={deletingId != null}
              onClick={() => setRulePendingDelete(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="esc-btn esc-btn--primary study-assignment-delete-confirm-btn"
              disabled={deletingId != null}
              onClick={() => rulePendingDelete && void handleDelete(rulePendingDelete)}
            >
              {deletingId != null ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
